import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BellRing, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { alertRinging, audioUnlocked, isMuted, onAlertChange, playChime, setMuted, startAlert, stopAlert, unlockAudio } from '../lib/chime';

// Driver + restaurant new-order sound: one-time browser unlock, mute switch and stop-ringing control.
export const SoundGate = () => {
  const { pathname } = useLocation();
  const [state, setState] = useState({ ringing: alertRinging(), unlocked: audioUnlocked(), muted: isMuted() });
  useEffect(() => onAlertChange(setState), []);
  const active = pathname.startsWith('/driver') || pathname.startsWith('/restaurant');
  if (!active) return null;
  const needsUnlock = !state.muted && !state.unlocked;
  if (!needsUnlock && !state.ringing) return null;
  return (
    <div data-testid="sound-gate" className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-6 z-[60] w-[min(92vw,420px)]">
      {needsUnlock ? (
        <button data-testid="sound-enable" onClick={async () => { await unlockAudio(); playChime(1); toast.success('Sonido de nuevos pedidos activado'); }}
          className="w-full h-12 rounded-2xl bg-[#1E2130] text-white text-sm font-bold flex items-center justify-center gap-2 shadow-lg press">
          <BellRing size={18} /> Activar sonido de nuevos pedidos
        </button>
      ) : (
        <div className="w-full rounded-2xl bg-[#EE3B36] text-white shadow-lg flex items-center gap-2 p-2.5">
          <BellRing size={20} className="animate-bounce shrink-0" />
          <p className="flex-1 text-sm font-bold">¡Nuevo pedido!</p>
          <button data-testid="sound-stop" onClick={stopAlert} className="h-9 px-3 rounded-xl bg-white/15 text-xs font-bold press">Silenciar aviso</button>
        </div>
      )}
    </div>
  );
};

export const SoundSettings = ({ testId = 'sound-settings' }) => {
  const [state, setState] = useState({ unlocked: audioUnlocked(), muted: isMuted() });
  useEffect(() => onAlertChange((s) => setState({ unlocked: s.unlocked, muted: s.muted })), []);
  const toggle = async () => { const next = !state.muted; setMuted(next); if (!next) await unlockAudio(); setState({ unlocked: audioUnlocked(), muted: next }); };
  const test = async () => { await unlockAudio(); if (!playChime()) toast.error('Tu navegador bloqueó el sonido. Toca la pantalla y vuelve a intentar.'); else toast.success('Así suena el aviso de nuevo pedido'); };
  return (
    <div data-testid={testId} className="rounded-2xl border border-gray-200 p-3.5 space-y-2.5">
      <p className="text-sm font-bold flex items-center gap-2">{state.muted ? <VolumeX size={17} className="text-gray-400" /> : <Volume2 size={17} className="text-emerald-600" />} Sonido de nuevos pedidos</p>
      <p className="text-xs text-gray-500">{state.muted ? 'Los avisos están silenciados.' : state.unlocked ? 'Activo: suena en cualquier pantalla al entrar un pedido nuevo.' : 'Toca "Probar sonido" una vez para que el navegador permita el audio.'}</p>
      <div className="flex gap-2">
        <button data-testid={`${testId}-test`} onClick={test} className="flex-1 h-10 rounded-xl bg-[#1E2130] text-white text-xs font-bold press">Probar sonido</button>
        <button data-testid={`${testId}-mute`} onClick={toggle} className={`flex-1 h-10 rounded-xl text-xs font-bold border press ${state.muted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-600 border-gray-200'}`}>{state.muted ? 'Activar avisos' : 'Silenciar avisos'}</button>
      </div>
      <button data-testid={`${testId}-demo`} onClick={() => startAlert(2)} className="w-full h-9 rounded-xl bg-[#FDECEC] text-[#EE3B36] text-xs font-bold press">Simular pedido nuevo</button>
    </div>
  );
};

export default SoundGate;
